import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Typography, useTheme } from '@mui/material';
import { AddSquare, MinusSquare } from 'iconsax-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTranslation } from 'react-i18next';
export function AnalysisComponent({ analysisData, onReferenceClick, onCloseDrawer }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [openItems, setOpenItems] = useState([]);
    useEffect(() => {
        if (analysisData && analysisData.length > 0) {
            // Initialize with all question numbers expanded
            setOpenItems(analysisData.map((qa) => String(qa.question_no)));
        }
    }, [analysisData]);
    const handleAccordionToggle = (id) => {
        setOpenItems((prevOpenItems) => {
            if (prevOpenItems.includes(id)) {
                return prevOpenItems.filter((item) => item !== id);
            }
            else {
                return [...prevOpenItems, id];
            }
        });
    };
    if (!analysisData || analysisData.length === 0) {
        return null;
    }
    return (_jsx(Box, { sx: {
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                borderLeft: `1px dashed #656565`
            },
            paddingLeft: '20px',
            marginLeft: '26px',
            marginBottom: '20px'
        }, children: analysisData.map((qa) => {
            const isExpanded = openItems.includes(String(qa.question_no));
            return (_jsxs(Accordion, { expanded: openItems.includes(String(qa.question_no)), onChange: () => handleAccordionToggle(String(qa.question_no)), sx: {
                    boxShadow: 'none',
                    '&.Mui-expanded': {
                        boxShadow: 'none',
                        // backgroundColor: alpha(theme.palette.secondary.main, 0.1)
                    }
                }, children: [_jsx(AccordionSummary, { sx: {
                            width: '100%',
                            '&.Mui-expanded .MuiTypography-root': {
                                color: theme.palette.text.primary
                            },
                            '&.Mui-expanded .questionText': {
                                color: `${theme.palette.grey[700]} !important`
                            }
                        }, children: _jsxs(Box, { width: '100%', children: [_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        width: '100%'
                                    }, children: [isExpanded ? (_jsx(MinusSquare, { color: theme.palette.secondary.dark })) : (_jsx(AddSquare, { color: theme.palette.secondary.dark })), _jsx(Box, { sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%'
                                            }, children: _jsx(Typography, { variant: 'h6', className: 'questionNumber', sx: { flex: 1, pr: 2, display: 'flex', alignItems: 'center', gap: 1 }, children: `${t('question')} ${qa.question_no}` }) })] }), _jsx(Box, { mt: 1, ml: 4, children: _jsx(Typography, { variant: 'body2', className: 'questionText', sx: { color: theme.palette.grey[700] }, children: _jsx(ReactMarkdown, { className: 'reactMarkDown', remarkPlugins: [remarkGfm], rehypePlugins: [rehypeRaw], children: qa.question }) }) })] }) }), _jsxs(AccordionDetails, { sx: {
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                borderLeft: `1px dashed ${theme.palette.secondary.dark}`
                            },
                            paddingLeft: '20px',
                            marginLeft: '26px',
                            marginBottom: '20px'
                        }, children: [_jsx(Typography, { variant: 'h6', textAlign: 'left', color: theme.palette.grey[700], children: t('answer') }), _jsx(Typography, { textAlign: 'left', variant: 'body2', color: theme.palette.grey[700], children: _jsx(ReactMarkdown, { className: 'reactMarkDown', remarkPlugins: [remarkGfm], rehypePlugins: [rehypeRaw], children: qa.answer }) }), _jsx(Typography, { variant: 'h6', textAlign: 'left', color: theme.palette.grey[700], mt: '20px', children: t('references') }), _jsx(Box, { display: 'flex', width: '100%', justifyContent: 'space-between', flexDirection: 'column', gap: 2, mt: 1, children: (() => {
                                    const map = new Map();
                                    qa?.references?.forEach((source) => {
                                        const docPath = source.document_path;
                                        if (!map.has(docPath)) {
                                            map.set(docPath, {
                                                doc: source,
                                                pages: [source.page_number]
                                            });
                                        }
                                        else {
                                            const entry = map.get(docPath);
                                            if (!entry.pages.includes(source.page_number)) {
                                                entry.pages.push(source.page_number);
                                            }
                                        }
                                    });
                                    return Array.from(map.values()).map(({ doc, pages }, index) => (_jsxs(Box, { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', children: [_jsx(Typography, { variant: 'subtitle2', mb: 0.5, pl: 1, sx: {
                                                    flex: 3,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    borderLeft: `1px solid ${theme.palette.secondary.main}`
                                                }, children: doc.document_name }), _jsx(Box, { display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', flex: 1, children: pages.map((page, pageIndex) => (_jsx(IconButton, { onClick: () => {
                                                        const fullSource = qa.references.find((s) => s.document_path === doc.document_path && s.page_number === page);
                                                        if (fullSource) {
                                                            onCloseDrawer && onCloseDrawer();
                                                            onReferenceClick?.(fullSource, qa.references);
                                                        }
                                                    }, sx: {
                                                        backgroundColor: theme.palette.secondary.main,
                                                        color: '#FFFFFF',
                                                        borderRadius: '50%',
                                                        width: 28,
                                                        height: 28,
                                                        fontSize: 12,
                                                        fontWeight: 'bold',
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.secondary.dark,
                                                            cursor: 'pointer'
                                                        }
                                                    }, children: page }, pageIndex))) })] }, index)));
                                })() })] })] }, qa.qa_pair_id));
        }) }));
}
